import {createAsyncThunk} from '@reduxjs/toolkit'
import {RouterState} from 'redux-first-history'

import {isApiError} from '@shared/helpers/api'
import {eq} from '@shared/helpers/lang'
import {SchemasUniversalDelivery} from '@shared/schemas/universal'

import {changeLocation, LocationChangeAction, systemAlert} from '@web/actions/application'
import {
    DELIVERY_INSTRUCTION_POST_MESSAGE_KEY,
    EntryMethod,
    EntryMethodType,
    PickUpLocation,
    PickUpLocationType,
} from '@web/core/constants'
import getApi, {getCommonBaseUrl} from '@web/helpers/api'
import {CommonState} from '@web/slices/common/slice'
import {DeliveryInfoState, updateDeliveryInstruction} from '@web/slices/deliveryInfo/slice'
import {orderSheetProductInfoSelector, OrderSheetProductsState} from '@web/slices/orderSheetProducts/slice'
import {locationSelector} from '@web/slices/router'

import apis from './apis'
import {
    AddressBookEntranceRequestFailMessage,
    DefaultAddressBookRequestFailMessage,
    ValidateSelectedZipCodeFailMessage,
    ValidateSelectedZipCodeFailType,
} from './constant'
import {DeliveryInstruction, ValidateAddressPayload} from './interfaces'

const createDeliveryInstruction = (deliveryInstructionForm: DeliveryInstruction) => {
    const {locationType, locationContent, entryMethodType, entryMethodContent, alwaysUses, directReceiveDisabled} =
        deliveryInstructionForm

    const eqLocationType = eq(locationType)
    const eqEntryMethodType = eq(entryMethodType)
    /** 수령위치가 기타 일 때 location method content를 저장 */
    const shouldUpdateLocationMethodContent = eqLocationType(PickUpLocation.OTHER)
    /** 수령위치가 문 앞 이면서, 자유 출입이 아닌 경우 entry method content를 저장 */
    const shouldUpdateEntryMethodContent =
        eqLocationType(PickUpLocation.FRONT_OF_DOOR) && !eqEntryMethodType(EntryMethod.FREE)

    return {
        location: {
            type: locationType ?? 'FRONT_OF_DOOR',
            content: shouldUpdateLocationMethodContent ? locationContent : '',
        },
        directReceiveDisabled,
        entryMethod: {
            type: entryMethodType,
            content: shouldUpdateEntryMethodContent ? entryMethodContent : '',
        },
        alwaysUses,
    }
}

const requestEntranceUpdate = async (
    pickup: {
        location: {
            type: PickUpLocationType
            content: string
        }
        entryMethod: {
            type: Maybe<EntryMethodType>
            content: string
        }
        alwaysUses: boolean
    },
    hash: string,
): Promise<string> => {
    try {
        const shouldDelete = !pickup.alwaysUses
        const shouldUpdateEntryMethodType = eq(PickUpLocation.FRONT_OF_DOOR, pickup.location.type)

        return shouldDelete
            ? (await apis.deleteEntrance({hash})).code
            : (
                  await apis.editEntrance({
                      hash,
                      entrance: {
                          locationCode: pickup.location.type,
                          locationDescription: pickup.location.content,
                          entranceCode: shouldUpdateEntryMethodType ? pickup.entryMethod.type : null,
                          entranceDescription: pickup.entryMethod.content,
                      },
                  })
              ).code
    } catch (e) {
        if (isApiError(e)) {
            return e.response?.data?.result?.code
        } else {
            throw e
        }
    }
}

interface FetchDeliveryInstructionPayload {
    deliveryInstruction: Maybe<{
        /** 수령위치 타입 */
        locationCode: PickUpLocationType
        /** 수령위치 기타 내용 */
        locationDescription?: string
        /** 출입방법 타입 */
        entranceCode: Maybe<EntryMethodType>
        /** 출입방법 내용 */
        entranceDescription?: string
    }>
}

export const fetchDeliveryInstruction = createAsyncThunk<
    FetchDeliveryInstructionPayload,
    {hash: string},
    {rejectValue: 'FETCH_DELIVERY_INSTRUCTION_FAILED'}
>('deliveryInstruction/fetch', async ({hash}, {rejectWithValue}) => {
    const api = getApi(getCommonBaseUrl())

    try {
        const {
            data: {
                result: {deliveryInstruction},
            },
        } = await api.get<ApigwResponseBase<SchemasUniversalDelivery['/deliveryInstruction']['response']>>(
            '/delivery/deliveryInstruction',
            {
                params: {hash},
            },
        )

        return {
            deliveryInstruction,
        }
    } catch {
        return rejectWithValue('FETCH_DELIVERY_INSTRUCTION_FAILED')
    }
})

interface ConfirmDeliveryInstructionEditProps {
    /** 수령위치/출입방법 정보 */
    deliveryInstructionForm: DeliveryInstruction
    /** 완료 후 이탈 방법 */
    leaveMethod: typeof LocationChangeAction.BACK | typeof LocationChangeAction.CLOSE
    /** 완료 후 데이터 처리 방법 */
    successAction: 'POST_MESSAGE' | 'UPDATE_STATE'
}

export const confirmDeliveryInstructionEdit = createAsyncThunk<void, ConfirmDeliveryInstructionEditProps>(
    'deliveryManager/confirmDeliveryInstructionEdit',
    async ({deliveryInstructionForm, leaveMethod, successAction}, {dispatch}) => {
        const deliveryInstruction = createDeliveryInstruction(deliveryInstructionForm)
        const resultCode = await requestEntranceUpdate(deliveryInstruction, deliveryInstructionForm.hash)
        const isSuccess = resultCode === '2001'

        if (isSuccess) {
            if (successAction === 'UPDATE_STATE') {
                dispatch(updateDeliveryInstruction(deliveryInstruction))
            } else {
                window.opener?.postMessage(
                    {
                        key: DELIVERY_INSTRUCTION_POST_MESSAGE_KEY,
                        content: deliveryInstruction,
                    },
                    '*',
                )
            }
        } else {
            dispatch(
                systemAlert({
                    type: 'ALERT',
                    message:
                        (resultCode === '4001' && AddressBookEntranceRequestFailMessage[resultCode]) ||
                        DefaultAddressBookRequestFailMessage,
                }),
            )
        }

        const shouldLeave = resultCode === '2001' || resultCode === '4001'
        if (shouldLeave) {
            dispatch(changeLocation({type: leaveMethod}))
        }
    },
)

/**
 * 우편번호 주소 검증 api
 * 주문 등록 / 새로고침 / 배송지 변경 / 결제하기 시에 주소를 검증합니다.
 * 주소 검증 실패 타입이 우편번호가 5자리 이면서 다음 세가지인 경우 주소 변경 제안 배너를 노출합니다.
 * - 해당 우편번호 검색결과 없음,
 * - 지번주소와 매칭되는 결과 없음
 * - 도로명주소와 매칭되는 결과 없음
 * 검증이 실패했어도 주문은 가능합니다.
 * 참고) 우편번호 6자리인 케이스에는 얼럿과 함께 배송지관리 screen/page를 노출하며 주문이 불가합니다.
 * 따라서 검증은 진행하지만 검증에 실패해도 변경 제안 배너를 미노출합니다.
 */
export const validateSelectedZipCodeAddress = createAsyncThunk<
    ValidateAddressPayload,
    string,
    {
        state: {
            common: CommonState
            deliveryInfo: DeliveryInfoState
            orderSheetProducts: OrderSheetProductsState
            router: RouterState
        }
        rejectValue: {
            type: string
            message: string
        }
    }
>('deliveryManager/validateSelectedZipCodeAddress', async (requestReason, {getState, rejectWithValue}) => {
    const state = getState()
    const {
        deliveryInfo: {
            selected: {addressInfo},
        },
    } = state
    const {hasArrivalGuaranteeProduct} = orderSheetProductInfoSelector(state)
    const {state: locationState} = locationSelector(state)

    if (!addressInfo) {
        return {
            isValid: false,
            validationFailType: null,
        }
    }

    const {zipCode, baseAddress, isRoadNameAddress: useRoadNameAddress} = addressInfo

    let validateAddressPayload: ValidateAddressPayload = {
        isValid: true,
        validationFailType: null,
    }

    try {
        const {validAddress, failReason} = await apis.validateAddress({
            zipCode,
            baseAddress,
            useRoadNameAddress,
            requestReason,
        })
        validateAddressPayload = {isValid: validAddress, validationFailType: failReason?.failType ?? null}
    } catch {}

    try {
        if (![0, 5].some((validZipCodeLength) => addressInfo?.zipCode.length === validZipCodeLength)) {
            const type = hasArrivalGuaranteeProduct
                ? ValidateSelectedZipCodeFailType.LOT_NUMBER_ADDRESS_ZIPCODE_WITH_ARRIVAL_GUARANTEE_DELIVERY
                : ValidateSelectedZipCodeFailType.LOT_NUMBER_ADDRESS_ZIPCODE

            // 배송지 목록이나 추가 수정등 dialog 가 노출중일때에는, alert 을 노출하지 않습니다.
            if (locationState.deliveryFullScreenDialogId) {
                return {
                    isValid: false,
                    validationFailType: null,
                }
            }

            return rejectWithValue({
                type,
                message: ValidateSelectedZipCodeFailMessage[type],
            })
        }

        return validateAddressPayload
    } catch (e) {
        if (isApiError(e)) {
            return rejectWithValue({
                type: ValidateSelectedZipCodeFailType.ROAD_NAME_ADDRESS_ZIPCODE_VALIDATION_API_FAIL,
                message:
                    ValidateSelectedZipCodeFailMessage[
                        ValidateSelectedZipCodeFailType.ROAD_NAME_ADDRESS_ZIPCODE_VALIDATION_API_FAIL
                    ],
            })
        }
        throw e
    }
})
